import { Button } from "../ui/button";
import { Link, usePath } from "../../lib/router";

interface SidebarProps {
  user: {
    firstName: string;
    lastName: string;
    role: string;
  };
  logout: () => void;
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ user, logout, isOpen, onClose }: SidebarProps) {
  const currentPath = usePath();

  const isActive = (path: string | string[]) => {
    if (Array.isArray(path)) {
      return path.some(
        (p) => currentPath === p || currentPath.startsWith(p + "/")
      );
    }
    return currentPath === path || currentPath.startsWith(path + "/");
  };

  const menuItemClass = (path: string | string[], baseClass: string = "") => {
    const active = isActive(path);
    return `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
      active
        ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md"
        : `hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 ${baseClass}`
    }`;
  };

  // Close sidebar on mobile after navigation
  const handleNavClick = () => {
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile Sidebar Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-white dark:bg-gray-800 shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <Link
              to="/"
              className="flex items-center gap-3"
              onClick={handleNavClick}
            >
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-xl">📋</span>
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  ระบบลางาน
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Leave Management
                </p>
              </div>
            </Link>
            {/* Close button for mobile */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 lg:hidden"
            >
              <span className="text-xl">✕</span>
            </button>
          </div>

          {/* Sidebar Menu */}
          <nav className="flex-1 overflow-y-auto p-4">
            <div className="space-y-1">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                เมนูหลัก
              </p>

              <Link
                to="/"
                className={menuItemClass("/")}
                onClick={handleNavClick}
              >
                <span className="text-xl">🏠</span>
                <span className="font-medium">หน้าหลัก</span>
              </Link>

              <Link
                to="/leave/request"
                className={menuItemClass(["/leave/request", "/leave/new"])}
                onClick={handleNavClick}
              >
                <span className="text-xl">📝</span>
                <span>ขอลางาน</span>
              </Link>

              <Link
                to="/leaves"
                className={menuItemClass(["/leaves", "/leave/list"])}
                onClick={handleNavClick}
              >
                <span className="text-xl">📋</span>
                <span>คำขอลาทั้งหมด</span>
              </Link>

              <Link
                to="/dashboard"
                className={menuItemClass("/dashboard")}
                onClick={handleNavClick}
              >
                <span className="text-xl">📊</span>
                <span>Dashboard</span>
              </Link>

              <Link
                to="/attendance/wfh"
                className={menuItemClass("/attendance/wfh")}
                onClick={handleNavClick}
              >
                <span className="text-xl">🏠</span>
                <span>WFH Check-in/out</span>
              </Link>

              <Link
                to="/calendar"
                className={menuItemClass(["/calendar", "/leave/calendar"])}
                onClick={handleNavClick}
              >
                <span className="text-xl">📅</span>
                <span>ปฏิทิน</span>
              </Link>
            </div>

            {(user.role === "admin" || user.role === "hr") && (
              <div className="mt-6 space-y-1">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  การจัดการ
                </p>

                <Link
                  to="/leave-balances"
                  className={menuItemClass(
                    ["/leave-balances", "/admin/leave-balances"],
                    "hover:bg-purple-50 dark:hover:bg-purple-900/30 text-purple-600 dark:text-purple-400"
                  )}
                  onClick={handleNavClick}
                >
                  <span className="text-xl">👥</span>
                  <span>ตั้งค่าสิทธิ์</span>
                </Link>

                <Link
                  to="/user-approval"
                  className={menuItemClass(
                    ["/user-approval", "/admin/user-approval"],
                    "hover:bg-purple-50 dark:hover:bg-purple-900/30 text-purple-600 dark:text-purple-400"
                  )}
                  onClick={handleNavClick}
                >
                  <span className="text-xl">✅</span>
                  <span>อนุมัติผู้ใช้</span>
                </Link>
              </div>
            )}

            {(user.role === "admin" ||
              user.role === "hr" ||
              user.role === "manager") && (
              <div className="mt-6 space-y-1">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  Attendance
                </p>

                <Link
                  to="/attendance/manage"
                  className={menuItemClass(
                    "/attendance/manage",
                    "hover:bg-sky-50 dark:hover:bg-sky-900/30 text-sky-600 dark:text-sky-400"
                  )}
                  onClick={handleNavClick}
                >
                  <span className="text-xl">🧭</span>
                  <span>จัดการ WFH</span>
                </Link>
              </div>
            )}

            {user.role === "admin" && (
              <div className="mt-6 space-y-1">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  ผู้ดูแลระบบ
                </p>

                <Link
                  to="/admin"
                  className={menuItemClass(
                    "/admin",
                    "hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400"
                  )}
                  onClick={handleNavClick}
                >
                  <span className="text-xl">⚙️</span>
                  <span>Admin Panel</span>
                </Link>

                <Link
                  to="/api-test"
                  className={menuItemClass("/api-test")}
                  onClick={handleNavClick}
                >
                  <span className="text-xl">🔧</span>
                  <span>API Test</span>
                </Link>

                <Link
                  to="/db-viewer"
                  className={menuItemClass("/db-viewer")}
                  onClick={handleNavClick}
                >
                  <span className="text-xl">💾</span>
                  <span>Database</span>
                </Link>
              </div>
            )}
          </nav>

          {/* Sidebar Footer - User Info */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold shadow-md">
                {user.firstName.charAt(0)}
                {user.lastName.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {user.firstName} {user.lastName}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                  {user.role}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => logout()}
              className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
              size="sm"
            >
              🚪 ออกจากระบบ
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
